import React from 'react';
import { motion } from 'framer-motion';
import { Activity, Brain, ClipboardCheck, GraduationCap, CheckCircle2, ArrowRight, Heart, Users, Star, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 30
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6,
    ease: 'easeOut'
  }
};
export function Services() {
  return <main className="pt-32" data-id="element-462">
      {/* Hero Section */}
      <section className="py-16 bg-medical-gray/30 relative overflow-hidden" data-id="element-463">
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10" data-id="element-464">
          <motion.h1 {...fadeInUp} className="text-5xl md:text-7xl font-heading font-extrabold mb-8 leading-tight" data-id="element-465">
            Integrated <span className="text-medical-red" data-id="element-466">Wellness</span>{' '}
            Solutions
          </motion.h1>
          <motion.p {...fadeInUp} className="text-lg md:text-xl text-medical-blue/70 max-w-3xl mx-auto leading-relaxed font-medium" data-id="element-467">
            We offer a comprehensive range of integrated medical and mental
            wellness services designed to restore your health and rebuild your
            emotional strength.
          </motion.p>
        </div>
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-[0]" data-id="element-468">
          <svg viewBox="0 0 1200 120" preserveAspectRatio="none" className="relative block w-full h-[60px] fill-white" data-id="element-469">
            <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V120H0V95.8C57.23,107.63,128.3,108.13,201.33,101.86,242.71,98.3,282.73,89.14,321.39,56.44Z" data-id="element-470"></path>
          </svg>
        </div>
      </section>

      {/* Detailed Services Grid */}
      <section className="py-32 bg-white" data-id="element-471">
        <div className="max-w-7xl mx-auto px-6" data-id="element-472">
          <div className="grid lg:grid-cols-2 gap-8 mb-24" data-id="element-473">
            {/* Medical & Wellness */}
            <motion.div {...fadeInUp} className="p-10 rounded-[40px] bg-medical-gray/30 border border-medical-gray flex flex-col" data-id="element-474">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-8 shadow-sm" data-id="element-475">
                <Activity className="text-medical-red" size={32} data-id="element-476" />
              </div>
              <h2 className="text-3xl font-heading font-extrabold text-medical-blue mb-8 italic" data-id="element-477">
                Family Practice & Lifestyle Wellness
              </h2>
              <ul className="grid gap-4 flex-grow" data-id="element-478">
                {['Preventive care and wellness exams', 'Chronic disease management', 'Lifestyle counseling', 'Comprehensive health evaluations'].map((s, i) => <li key={i} className="flex items-center gap-3 text-lg font-semibold text-medical-blue/80" data-id="element-479">
                    <CheckCircle2 size={20} className="text-medical-red shrink-0" data-id="element-480" />{' '}
                    {s}
                  </li>)}
              </ul>
            </motion.div>

            {/* Health Risk Assessments */}
            <motion.div {...fadeInUp} transition={{
            delay: 0.1
          }} className="p-10 rounded-[40px] bg-medical-blue text-white shadow-2xl flex flex-col" data-id="element-481">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-8 shadow-sm" data-id="element-482">
                <Shield className="text-medical-red" size={32} data-id="element-483" />
              </div>
              <h2 className="text-3xl font-heading font-extrabold mb-8 italic" data-id="element-484">
                Health Risk Assessments (HRAs)
              </h2>
              <ul className="grid gap-4 flex-grow" data-id="element-485">
                {['Comprehensive health evaluations', 'Risk identification', 'Preventive planning', 'Clinical consultation'].map((s, i) => <li key={i} className="flex items-center gap-3 text-lg font-semibold opacity-90" data-id="element-486">
                    <CheckCircle2 size={20} className="text-medical-red shrink-0" data-id="element-487" />{' '}
                    {s}
                  </li>)}
              </ul>
            </motion.div>

            {/* Consultation & Training */}
            <motion.div {...fadeInUp} transition={{
            delay: 0.2
          }} className="p-10 rounded-[40px] bg-medical-gray/30 border border-medical-gray flex flex-col" data-id="element-488">
              <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mb-8 shadow-sm" data-id="element-489">
                <ClipboardCheck className="text-medical-red" size={32} data-id="element-490" />
              </div>
              <h2 className="text-3xl font-heading font-extrabold text-medical-blue mb-8 italic" data-id="element-491">
                Consultation & Professional Training
              </h2>
              <ul className="grid gap-4 flex-grow" data-id="element-492">
                {['Healthcare consultation', 'Mental wellness workshops', 'Organizational wellness programs', 'Mental health awareness training'].map((s, i) => <li key={i} className="flex items-center gap-3 text-lg font-semibold text-medical-blue/80" data-id="element-493">
                    <CheckCircle2 size={20} className="text-medical-red shrink-0" data-id="element-494" />{' '}
                    {s}
                  </li>)}
              </ul>
            </motion.div>

            {/* Mental Health Coaching */}
            <motion.div {...fadeInUp} transition={{
            delay: 0.3
          }} className="p-10 rounded-[40px] bg-medical-blue text-white shadow-2xl flex flex-col" data-id="element-495">
              <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-8 shadow-sm" data-id="element-496">
                <Brain className="text-medical-red" size={32} data-id="element-497" />
              </div>
              <h2 className="text-3xl font-heading font-extrabold mb-8 italic" data-id="element-498">
                Mental Health Coaching Services
              </h2>
              <ul className="grid gap-4 flex-grow" data-id="element-499">
                {['Marriage and relationship restoration', 'Trauma and PTSD recovery support', 'Anxiety and Depression support', 'Life recovery and purpose coaching'].map((s, i) => <li key={i} className="flex items-center gap-3 text-lg font-semibold opacity-90" data-id="element-500">
                    <CheckCircle2 size={20} className="text-medical-red shrink-0" data-id="element-501" />{' '}
                    {s}
                  </li>)}
              </ul>
            </motion.div>
          </div>

          {/* Visual Sections with New Images */}
          <div className="space-y-32" data-id="element-502">
            <div className="grid lg:grid-cols-2 gap-20 items-center" data-id="element-503">
              <motion.div {...fadeInUp} className="relative" data-id="element-504">
                <div className="rounded-[60px] overflow-hidden shadow-2xl border-8 border-medical-gray/30 aspect-square" data-id="element-505">
                  <img src="https://cdn.magicpatterns.com/uploads/kwpy3pSbunAaXqHD4LqUE2/global_1.png" alt="Nutrition and Lifestyle" className="w-full h-full object-cover" data-id="element-506" />
                </div>
              </motion.div>
              <motion.div {...fadeInUp} data-id="element-507">
                <h2 className="text-4xl font-heading font-extrabold text-medical-blue mb-6 italic" data-id="element-508">
                  Holistic Nutrition & Lifestyle
                </h2>
                <p className="text-xl text-medical-blue/70 mb-8 leading-relaxed" data-id="element-509">
                  Our medical wellness approach integrates clinical expertise
                  with lifestyle counseling to help you rebuild your health from
                  the inside out.
                </p>
                <Link to="/contact" className="inline-flex items-center gap-2 text-medical-red font-bold hover:gap-4 transition-all" data-id="element-510">
                  Book a Consultation <ArrowRight size={20} data-id="element-511" />
                </Link>
              </motion.div>
            </div>

            <div className="grid lg:grid-cols-2 gap-20 items-center" data-id="element-512">
              <motion.div {...fadeInUp} className="order-2 lg:order-1" data-id="element-513">
                <h2 className="text-4xl font-heading font-extrabold text-medical-blue mb-6 italic" data-id="element-514">
                  Emotional Healing & Resilience
                </h2>
                <p className="text-xl text-medical-blue/70 mb-8 leading-relaxed" data-id="element-515">
                  Specialized coaching for trauma recovery, anxiety, and life
                  transitions, designed to restore your mental clarity and
                  purpose.
                </p>
                <Link to="/contact" className="inline-flex items-center gap-2 text-medical-red font-bold hover:gap-4 transition-all" data-id="element-516">
                  Start Your Journey <ArrowRight size={20} data-id="element-517" />
                </Link>
              </motion.div>
              <motion.div {...fadeInUp} className="relative order-1 lg:order-2" data-id="element-518">
                <div className="rounded-[60px] overflow-hidden shadow-2xl border-8 border-medical-gray/30 aspect-square" data-id="element-519">
                  <img src="https://cdn.magicpatterns.com/uploads/j9UxNAHbLcjVrfMMUSd89S/global_2.png" alt="Mental Wellness" className="w-full h-full object-cover" data-id="element-520" />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-medical-red text-white text-center relative overflow-hidden" data-id="element-521">
        <div className="max-w-4xl mx-auto px-6 relative z-10" data-id="element-522">
          <h2 className="text-4xl md:text-6xl font-heading font-extrabold mb-8" data-id="element-523">
            Ready to Start?
          </h2>
          <p className="text-xl opacity-90 mb-12 leading-relaxed" data-id="element-524">
            Book your free 20-minute consultation today and begin your journey
            to restorative health.
          </p>
          <Link to="/contact" className="inline-flex items-center gap-3 bg-white text-medical-red px-12 py-6 rounded-full font-heading font-extrabold text-xl hover:bg-medical-blue hover:text-white transition-all shadow-2xl shadow-black/20" data-id="element-525">
            Book Consultation <ArrowRight size={24} data-id="element-526" />
          </Link>
        </div>
      </section>
    </main>;
}